import { useCallback } from "react";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import { Spinner } from "react-bootstrap";
import { CreateTokenSessionReq, HelcimPaymentReturnRequest } from "../../../../interfaces";
import { createHelcimCheckoutTokenAsync, savePaymentMethodAsync } from "../../../../services/PaymentServices/HelimCustomerService";
import { useAuth } from "../../../../firebase/AuthProvider";
import { loadHelcimPayScript } from "../../../../utilities/helcim/loadHelcimPayScript";

interface PaymentMethodAdderProps {
    methodType: "cc" | "ach";
    buttonText: string;
    icon: React.ReactNode;
    reload: () => void;
}

const PaymentMethodAdder: React.FC<PaymentMethodAdderProps> = ({ methodType, buttonText, icon, reload }) => {
    const { currentUser, currentUserInfo } = useAuth();
    const [checkoutToken, setCheckoutToken] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    // Set up Helcim message listener
    const savePaymentMethod = useCallback(async (payload: HelcimPaymentReturnRequest) => {
        if (!currentUser) {
            throw new Error("User not authenticated. Please login again.");
        }
        console.log("Payload to backend:", payload);
        const authToken = await currentUser.getIdToken();

        const saveResponse = await savePaymentMethodAsync(authToken, payload);

        if (!saveResponse.success) {
            throw new Error(saveResponse.message || "Failed to store payment method");
        }

        console.log("Backend response:", saveResponse.data);
    }, [currentUser]);

useEffect(() => {
        const handleHelcimMessage = async (event: MessageEvent) => {
            if (!event.data || !checkoutToken) return;

            const helcimIdentifier = `helcim-pay-js-${checkoutToken}`;

            if (event.data.eventName !== helcimIdentifier) return;

            console.log("Helcim Event:", event.data);

            // Payment Success
            if (event.data.eventStatus === "SUCCESS") {
                try {
                    const transactionResponse = JSON.parse(event.data.eventMessage);
                    console.log("Payment Success:", transactionResponse);

                    const responseData = transactionResponse?.data?.data;

                    if (!responseData) {
                        throw new Error("Invalid Helcim response");
                    }

                    // Detect payment method
                    const isCard = !!responseData.cardToken;
                    const isBank = !!responseData.bankAccount;

                    let payload: HelcimPaymentReturnRequest = {
                        customerCode: "",
                        bankToken: null,
                        cardToken: null,
                        paymentMethod: "",
                        last4: ""
                    };

                    if (isCard) {
                        payload = {
                            customerCode: responseData.customerCode,
                            cardToken: responseData.cardToken,
                            bankToken: null,
                            paymentMethod: "card",
                            last4: responseData.cardNumber?.slice(-4)
                        };
                    }

                    if (isBank) {
                        payload = {
                            customerCode: responseData.bankAccount.customerCode,
                            cardToken: null,
                            bankToken: responseData.bankAccount.bankToken,
                            paymentMethod: "ach",
                            last4: responseData.bankAccount.bankAccountNumber?.slice(-4)
                        };
                    }

                    if (!currentUser) {
                        throw new Error("User not authenticated. Please login again.");
                    }

                    // Save payment method
                    await savePaymentMethod(payload);

                    if (window.removeHelcimPayIframe) {
                        window.removeHelcimPayIframe();
                    }

                    toast.success("Payment method added successfully!");
                    setCheckoutToken(null);
                    reload();

                } catch (err) {
                    console.error("Payment processing error:", err);
                    toast.error("Payment succeeded but saving failed.");
                } finally {
                    setLoading(false);
                }
            }

            // Payment Failed
            if (event.data.eventStatus === "ABORTED") {
                console.error("Transaction failed:", event.data.eventMessage);
                toast.error("Payment failed. Please try again.");
                setLoading(false);
            }

            // User closed modal
            if (event.data.eventStatus === "HIDE") {
                console.log("User closed Helcim modal.");
                setLoading(false);
            }
        };

        window.addEventListener("message", handleHelcimMessage);

        return () => {
            window.removeEventListener("message", handleHelcimMessage);
        };
    }, [checkoutToken, currentUser, savePaymentMethod, reload]);

    // Helper to save payment method with the backend


    const createHelcimCheckoutToken = async () => {
        try {
            if (!currentUser) return;
            setLoading(true);
            await loadHelcimPayScript();
            const token = await currentUser.getIdToken();

            const payload: CreateTokenSessionReq = {
                methodType: methodType
            };
            const createTokenRes = await createHelcimCheckoutTokenAsync(token, payload);

            if (createTokenRes.success) {
                setCheckoutToken(createTokenRes.data);

                // Append Helcim iframe
                if (window.appendHelcimPayIframe) {
                    const userEmail = currentUserInfo?.email || "user@example.com";
                    window.appendHelcimPayIframe(createTokenRes.data, true, userEmail);
                    toast.info(
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <Spinner size="sm" animation="border" />
                            <span>Initializing payment verification...</span>
                        </div>,
                        { closeButton: true, autoClose: 5000 }
                    );
                } else {
                    console.error("Helcim script (start.js) not loaded.");
                    toast.error("Unable to load payment iframe. Please refresh and try again.");
                    setLoading(false);
                }
            }

        } catch (error: any) {
            if (error.name === 'AbortError') return;
            toast.error(error instanceof AxiosError ? error.response?.data.message : error.message || "Error to get the data");
            setLoading(false);
        }
    };

    return (
        <button
            className="pm_btn"
            onClick={createHelcimCheckoutToken}
            disabled={loading}
        >
            {loading ? (
                <><Spinner size="sm" /> Adding...</>
            ) : (
                <>{icon} {buttonText}</>
            )}
        </button>
    );
};

export default PaymentMethodAdder;
